import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { api, API } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";

const Icon = ({ name, ...p }) => {
  const map = { code: "Code2", users: "Users", crown: "Crown", "badge-check": "BadgeCheck",
    clock: "Clock", layers: "Layers", "user-check": "UserCheck", zap: "Zap",
    "shield-check": "ShieldCheck", "message-square": "MessageSquare", map: "Map",
    handshake: "Handshake", briefcase: "Briefcase", "check-circle": "CheckCircle2" };
  const C = Icons[map[name] || "Briefcase"] || Icons.Briefcase;
  return <C {...p} />;
};

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.07 } }) };

export default function Home() {
  const [c, setC] = useState(null);
  const [hubPosts, setHubPosts] = useState([]);
  useEffect(() => {
    api.get("/content").then((r) => setC(r.data)).catch(() => {});
    api.get("/hub").then((r) => setHubPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);
  if (!c) return <div className="min-h-screen flex items-center justify-center text-navy font-display">Loading…</div>;

  return (
    <div id="home">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-navy text-white">
        <div className="absolute inset-0 grain-band opacity-40" />
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center relative">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <span className="inline-block text-xs tracking-[0.25em] uppercase text-emerald-light font-medium mb-5">Recruitment. Redefined.</span>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">{c.hero_heading}</h1>
            <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">{c.hero_subheading}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/hire-talent" data-testid="hero-hire-talent" className="inline-flex items-center gap-2 bg-emerald-brand text-white font-semibold rounded-full px-7 py-3.5 hover:bg-emerald-dark transition-colors active:scale-95">{c.hero_cta_primary} <ArrowRight size={18} /></Link>
              <button onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-explore-services" className="inline-flex items-center gap-2 border-2 border-white/70 text-white font-semibold rounded-full px-7 py-3.5 hover:bg-white hover:text-navy transition-colors">{c.hero_cta_secondary}</button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
            <img src={c.hero_image ? `${API}/media/${c.hero_image}` : "https://images.pexels.com/photos/1181738/pexels-photo-1181738.jpeg"} alt="Professionals in a meeting" className="rounded-3xl w-full h-[420px] object-cover shadow-2xl" />
            <div className="absolute -bottom-5 -left-5 bg-white text-navy rounded-2xl px-6 py-4 shadow-xl hidden sm:block">
              <div className="text-2xl font-display font-bold">{c.hero_badge_title || "Pan-India"}</div>
              <div className="text-xs text-slate-500">{c.hero_badge_subtitle || "Talent Network"}</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <img src={c.about_image ? `${API}/media/${c.about_image}` : "https://images.unsplash.com/photo-1698047682091-782b1e5c6536"} alt="Recruitment partnership" className="rounded-3xl w-full h-[400px] object-cover order-2 lg:order-1" />
          <div className="order-1 lg:order-2">
            <span className="text-xs tracking-[0.25em] uppercase text-emerald-brand font-medium">About IWS</span>
            <h2 className="mt-3 font-display font-semibold text-3xl lg:text-4xl tracking-tight text-navy">{c.about_title}</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{c.about_text}</p>
            <div className="mt-7 grid grid-cols-2 gap-4">
              {c.why_choose.slice(0, 4).map((w) => (
                <div key={w.title} className="flex items-start gap-2"><CheckCircle2 className="text-emerald-brand shrink-0 mt-0.5" size={18} /><span className="text-sm font-medium text-navy">{w.title}</span></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 lg:py-28 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-xs tracking-[0.25em] uppercase text-emerald-brand font-medium">What We Do</span>
          <h2 className="mt-3 font-display font-semibold text-3xl lg:text-4xl tracking-tight text-navy">Our Services</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.services.map((s, i) => (
              <motion.div key={s.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
                data-testid={`service-card-${i}`} className="group bg-background rounded-2xl border border-slate-200 p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="h-12 w-12 rounded-xl bg-navy/5 flex items-center justify-center text-navy group-hover:bg-emerald-brand group-hover:text-white transition-colors"><Icon name={s.icon} size={22} /></div>
                <h3 className="mt-5 font-display font-semibold text-xl text-navy">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-xs tracking-[0.25em] uppercase text-emerald-brand font-medium">The IWS Edge</span>
          <h2 className="mt-3 font-display font-semibold text-3xl lg:text-4xl tracking-tight text-navy">Why Choose IWS</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.why_choose.map((w, i) => (
              <div key={w.title} data-testid={`why-card-${i}`} className="flex gap-4 p-6 rounded-2xl bg-white border border-slate-200">
                <div className="h-11 w-11 shrink-0 rounded-lg bg-emerald-brand/10 text-emerald-brand flex items-center justify-center"><Icon name={w.icon} size={20} /></div>
                <div><h3 className="font-display font-semibold text-navy">{w.title}</h3><p className="mt-1 text-sm text-slate-600">{w.description}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="py-20 lg:py-28 bg-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 grain-band opacity-30" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <span className="text-xs tracking-[0.25em] uppercase text-emerald-light font-medium">How It Works</span>
          <h2 className="mt-3 font-display font-semibold text-3xl lg:text-4xl tracking-tight">Our Recruitment Process</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-5 gap-6">
            {c.process.map((p, i) => (
              <motion.div key={p.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} data-testid={`process-step-${i}`} className="relative">
                <div className="h-12 w-12 rounded-full bg-emerald-brand flex items-center justify-center font-display font-bold text-lg">{i + 1}</div>
                <h3 className="mt-4 font-display font-semibold text-lg">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-300">{p.description}</p>
                {i < c.process.length - 1 && <div className="hidden md:block absolute top-6 left-14 right-0 h-px bg-white/20" />}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-xs tracking-[0.25em] uppercase text-emerald-brand font-medium">Sectors We Serve</span>
          <h2 className="mt-3 font-display font-semibold text-3xl lg:text-4xl tracking-tight text-navy">Industries Served</h2>
          <div className="mt-12 flex flex-wrap gap-3">
            {c.industries.map((ind, i) => (
              <div key={ind} data-testid={`industry-${i}`} className="px-5 py-3 rounded-full bg-white border border-slate-200 text-navy font-medium text-sm hover:border-emerald-brand hover:text-emerald-brand transition-colors cursor-default">{ind}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Knowledge Hub strip */}
      {hubPosts.length > 0 && (
        <section className="py-20 lg:py-24 bg-white border-t border-slate-200" data-testid="home-hub-strip">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between gap-4 mb-10">
              <div>
                <span className="text-xs tracking-[0.25em] uppercase text-emerald-brand font-medium">Knowledge Hub</span>
                <h2 className="mt-3 font-display font-semibold text-3xl lg:text-4xl tracking-tight text-navy">Latest Insights</h2>
              </div>
              <Link to="/hub" data-testid="home-hub-all" className="hidden sm:inline-flex items-center gap-1.5 text-navy font-semibold hover:text-emerald-brand transition-colors">View all <ArrowRight size={16} /></Link>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {hubPosts.map((p, i) => {
                const img = p.type === "video" ? (p.video_thumbnail || p.cover_image) : p.cover_image;
                return (
                  <Link key={p.id} to={`/hub/${p.slug}`} data-testid={`home-hub-card-${i}`} className="group bg-background rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                    <div className="aspect-[16/10] bg-navy/5 overflow-hidden">{img ? <img src={img} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="h-full w-full flex items-center justify-center text-navy/20"><Icons.FileText size={36} /></div>}</div>
                    <div className="p-5">
                      {p.category && <span className="text-xs font-medium text-emerald-brand">{p.category}</span>}
                      <h3 className="mt-1.5 font-display font-semibold text-navy leading-snug line-clamp-2 group-hover:text-emerald-brand transition-colors">{p.title}</h3>
                      <span className="mt-3 inline-block text-xs text-slate-400">{p.reading_time || 1} min read</span>
                    </div>
                  </Link>
                );
              })}
            </div>
            <Link to="/hub" className="sm:hidden mt-6 inline-flex items-center gap-1.5 text-navy font-semibold hover:text-emerald-brand transition-colors">View all <ArrowRight size={16} /></Link>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-3xl bg-emerald-brand text-white px-8 py-14 lg:px-16 lg:py-16 relative overflow-hidden">
            <div className="absolute inset-0 grain-band opacity-30" />
            <div className="relative flex flex-col lg:flex-row items-center justify-between gap-6">
              <div>
                <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight">Ready to Build Your Team?</h2>
                <p className="mt-3 text-emerald-50 max-w-xl">Tell us your hiring needs and get a curated shortlist of pre-screened candidates.</p>
              </div>
              <Link to="/hire-talent" data-testid="cta-hire-today" className="inline-flex items-center gap-2 bg-navy text-white font-semibold rounded-full px-8 py-4 hover:bg-navy-900 transition-colors active:scale-95 shrink-0">Hire Talent Today <ArrowRight size={18} /></Link>
            </div>
          </div>
        </div>
      </section>

      <Footer content={c} />
    </div>
  );
}
